import type { SpaceCommitment, SpaceCommitments, SpaceDetail } from '@/types/spaces';
import { spaceHrefForPeriod } from './navigation';
import { participantName } from './settlement';

export type DueTone = 'error' | 'warning' | 'neutral';

// One look for a due pill wherever it shows (the Despesas panel, the home's attention rows and Compromissos).
export const DUE_TONE_CLASSES: Record<DueTone, string> = {
  error: 'bg-error-surface text-error-text',
  warning: 'bg-warning-surface text-warning-text',
  neutral: 'bg-surface-sunken text-text-secondary',
};

const DAY_MS = 86_400_000;
const CADENCE_LABELS: Record<SpaceCommitment['cadence'], string | null> = { weekly: 'semanal', monthly: 'mensal', yearly: 'anual', unknown: null };
const STATUSES: SpaceCommitment['status'][] = ['entered', 'upcoming', 'overdue'];

// Commitments are monthly; a trip settles as a whole and has no month to ask about.
export function hasCommitments(kind: string): boolean {
  return kind !== 'trip';
}

// Where every "A vencer" link leads: the Despesas page with the commitments panel open.
// The commitments a card shows belong to its period; Despesas › A vencer must open on that same month.
export function upcomingHref(space: Pick<SpaceDetail, 'id'>, period: string): string {
  return spaceHrefForPeriod(space.id, 'expenses', period, { filter: 'upcoming' });
}

export function isPending(commitment: Pick<SpaceCommitment, 'status'>): boolean {
  return commitment.status === 'upcoming' || commitment.status === 'overdue';
}

// The API keeps the name of someone who left, so they still read by name rather than by id.
export function commitmentResponsible(space: SpaceDetail, commitment: Pick<SpaceCommitment, 'responsibleUserId' | 'responsibleName'>): string {
  const current = space.members.some((member) => member.userId === commitment.responsibleUserId);
  return (!current && commitment.responsibleName?.trim()) || participantName(space, commitment.responsibleUserId);
}

export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

// `today` is the UTC day the space pages share (`useUtcDay`); the API's overdue flips on the same clock.
export function dueLabel(dueDate: string | null, today: string): { text: string; tone: DueTone } {
  if (!dueDate) return { text: 'a vencer', tone: 'neutral' };
  const days = Math.round((Date.parse(dueDate.slice(0, 10)) - Date.parse(today)) / DAY_MS);
  if (days < 0) return { text: `venceu há ${-days}d`, tone: 'error' };
  if (days === 0) return { text: 'vence hoje', tone: 'warning' };
  if (days <= 7) return { text: `vence em ${days}d`, tone: 'warning' };
  return { text: `vence ${shortDate(dueDate)}`, tone: 'neutral' };
}

// The pill of a pending occurrence. The API only says "paid" to the person who paid, and only waits on a card charge
// while the bank may still publish it, so both outrank the due date; a plain due date comes last.
export function pendingLabel(commitment: Pick<SpaceCommitment, 'dueDate' | 'awaitingConfirmation' | 'paidAt'>, today: string): { text: string; tone: DueTone } {
  // Only the payer's own read carries `paidAt`, so "você" always names the reader.
  if (commitment.paidAt) return { text: `você pagou ${shortDate(commitment.paidAt)}`, tone: 'warning' };
  if (commitment.awaitingConfirmation) return { text: 'aguardando confirmação', tone: 'neutral' };
  return dueLabel(commitment.dueDate, today);
}

// "7 de 10" for a plan; the total alone can be unknown, and everything that is not a plan has neither.
export function installmentLabel(commitment: Pick<SpaceCommitment, 'installmentNumber' | 'installmentTotal'>): string | null {
  if (commitment.installmentNumber === null) return null;
  return commitment.installmentTotal === null ? `parcela ${commitment.installmentNumber}` : `parcela ${commitment.installmentNumber} de ${commitment.installmentTotal}`;
}

export function commitmentMeta(commitment: SpaceCommitment, category: string | undefined, today: string): string {
  // "próxima" only fits a date still ahead, judged on the same day as the pill beside it: a past date is already
  // told by that pill, whatever status the body was fetched with.
  const ahead = commitment.status === 'upcoming' && commitment.dueDate !== null && commitment.dueDate.slice(0, 10) >= today;
  const due = ahead ? `próxima ${shortDate(commitment.dueDate!)}` : null;
  const entered = commitment.status === 'entered' ? 'esta ocorrência já está nas despesas' : null;
  return [category, installmentLabel(commitment) ?? CADENCE_LABELS[commitment.cadence], due, entered].filter(Boolean).join(' · ');
}

export function commitmentGroups(data: SpaceCommitments): { pending: SpaceCommitment[]; entered: SpaceCommitment[]; monthlyCents: number } {
  return {
    pending: data.commitments.filter(isPending),
    entered: data.commitments.filter((commitment) => commitment.status === 'entered'),
    monthlyCents: data.commitments.reduce((sum, commitment) => sum + commitment.amountCents, 0),
  };
}

const isText = (value: unknown): value is string => typeof value === 'string';
const isOptionalText = (value: unknown): boolean => value === null || isText(value);
const isCount = (value: unknown): boolean => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const isOptionalCount = (value: unknown): boolean => value === null || isCount(value);
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// Every field of the body is checked, the ones this page does not use yet included, so the guard can honestly narrow
// to `SpaceCommitments`: a 200 of the wrong shape must read as a failed load, never throw.
export function isCommitments(data: unknown): data is SpaceCommitments {
  return isRecord(data) && isText(data.periodKey) && Array.isArray(data.commitments) && data.commitments.every(isCommitment)
    && Array.isArray(data.responsibilities) && data.responsibilities.every(isResponsibility);
}

function isResponsibility(row: unknown): boolean {
  return isRecord(row) && isText(row.userId) && isOptionalText(row.name) && isCount(row.entered) && isCount(row.total);
}

function isCommitment(row: unknown): boolean {
  if (!isRecord(row) || !isText(row.id) || !isText(row.name) || !isText(row.responsibleUserId)) return false;
  // `Object.hasOwn` coerces its key (`['monthly']` would pass as 'monthly'), so the value must be text first.
  if (!isText(row.cadence) || !Object.hasOwn(CADENCE_LABELS, row.cadence) || !STATUSES.includes(row.status as SpaceCommitment['status'])) return false;
  if (typeof row.amountCents !== 'number' || !Number.isFinite(row.amountCents)) return false;
  if (!isOptionalText(row.categoryId) || !isOptionalText(row.responsibleName) || !isDate(row.dueDate) || !isDate(row.enteredAt)) return false;
  if (typeof row.awaitingConfirmation !== 'boolean' || !isDate(row.paidAt)) return false;
  if (!Array.isArray(row.unsentTransactionIds) || !row.unsentTransactionIds.every(isText)) return false;
  return isOptionalCount(row.installmentNumber) && isOptionalCount(row.installmentTotal);
}

const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

// The API sends UTC instants. Parseable is not enough: `Date.parse` rolls an impossible day over (February 30th
// becomes March 2nd), which would show a row under the wrong date, so the calendar day must survive a round trip.
export function isUtcInstant(value: unknown): value is string {
  if (!isText(value) || !UTC_INSTANT.test(value)) return false;
  const time = Date.parse(value);
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value.slice(0, 10);
}

const isDate = (value: unknown): boolean => value === null || isUtcInstant(value);
